import {Feature} from '@hconnect/common/enums'
import {PlantNode} from '@hconnect/common/types'
import {useUserContext} from '@hconnect/login'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {useOverridenMillIds} from '../../../common/hooks'
import {useUrlParam} from '../../../routing'
import {useMaterialStorageListQuery} from '../../../storages/hooks'
import {removeProductionLinesFromTree, getEnrichedPlantStructure} from '../../helpers'
import {useSettingsQuery} from '../../providers'
import {useSettingsFeature} from '../useSettingsFeature'

import {useAssetsQuery} from './useAssetsQuery'
import {addOverridden} from './usePlantStructureQuery'

export const useGetRemovedPlantStructure = ({
  includeEquipment,
  product,
  upmId,
  showEnglishTranslation
}: {
  includeEquipment: boolean
  product?: string
  upmId?: string | undefined
  showEnglishTranslation?: boolean
}) => {
  const plantCode = useUrlParam('plantCode')
  const {loggedUser} = useUserContext()
  const {t} = useTranslation()

  // Feature toggle which controls if we display the full plant structure or only the plant node
  const isUpmStructureEnabled = useSettingsFeature(Feature.UpmAssetsAndStorages)

  // The boolean used to bypass the upm feature toggle for users with ASSET_CONFIGURATOR or JANUS_DEVELOPER role
  // and display full plant structure even for the plants without upm feature enabled
  const shouldBypassFeatureToggle = !!loggedUser?.hasRoles.some((role: string) =>
    ['ASSET_CONFIGURATOR', 'JANUS_DEVELOPER'].includes(role)
  )

  const {data: assets} = useAssetsQuery()

  const {data: overridenNodes} = useOverridenMillIds(upmId)
  const {data: materialStorages} = useMaterialStorageListQuery()

  // Wait for dependent queries (assets, material storages) before enabling
  const queriesReady = !!(assets && materialStorages)

  const transformPlantStructure = useCallback(
    (data: PlantNode) => {
      const rawData = addOverridden(data, overridenNodes || [])

      const enrichedPlantStructure = removeProductionLinesFromTree(
        getEnrichedPlantStructure(
          rawData,
          assets!,
          materialStorages!,
          '',
          t,
          showEnglishTranslation
        )
      )

      if (!isUpmStructureEnabled && !shouldBypassFeatureToggle) {
        return {...enrichedPlantStructure, children: []}
      }

      return enrichedPlantStructure
    },
    [
      assets,
      materialStorages,
      t,
      overridenNodes,
      isUpmStructureEnabled,
      shouldBypassFeatureToggle,
      showEnglishTranslation
    ]
  )

  return useSettingsQuery('upmRemovedStructure', [plantCode, includeEquipment, product], {
    staleTime: Infinity,
    placeholderData: (prev) => prev,
    select: transformPlantStructure,
    enabled: queriesReady
  })
}
